import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
  Sparkles,
  Car,
  Gem,
  ShieldCheck,
  Award,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Check,
  ArrowRight,
} from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { PromoBanner } from '@/components/PromoBanner';
import { PACKAGES, TESTIMONIALS, IMAGES, GALLERY_IMAGES } from '@/lib/data';
import { LUXE } from '@/constants/testIds';

const services = [
  {
    testId: LUXE.serviceInterior,
    title: 'Interior Detailing',
    desc: 'Immaculate cabins — deep vacuum, stain treatment, and revitalised leather.',
    icon: Car,
    image: IMAGES.interiorDetail,
    to: '/services',
  },
  {
    testId: LUXE.serviceExterior,
    title: 'Exterior Detailing',
    desc: 'Hand-washed, clay-bar smoothed, sealant-protected showroom finishes.',
    icon: Sparkles,
    image: IMAGES.exteriorPolish,
    to: '/services',
  },
  {
    testId: LUXE.servicePremium,
    title: 'Full Premium Packages',
    desc: 'Complete restoration — machine polish, engine bay, odor removal.',
    icon: Gem,
    image: IMAGES.premiumWash,
    to: '/services',
  },
];

const HomePage = () => {
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const total = TESTIMONIALS.length;

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div
          className="absolute inset-0 hero-parallax"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F1A]/60 via-[#0B0F1A]/75 to-[#0B0F1A]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(201,162,74,0.18),transparent_60%)]" />
        <div className="grain-overlay" />

        <div className="luxe-container relative z-10 pt-32 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8 fade-up">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-px bg-[#C9A24A]" />
              <span className="luxe-eyebrow">St. Louis · Est. Excellence</span>
            </div>
            <h1
              data-testid={LUXE.heroHeadline}
              className="text-4xl sm:text-5xl lg:text-7xl font-normal leading-[1.05] text-white max-w-5xl"
            >
              Luxury Auto Detailing That{' '}
              <span className="italic text-shimmer">Restores</span>
              <br />
              Showroom Perfection.
            </h1>
            <p className="mt-8 text-white/70 max-w-2xl text-base sm:text-lg leading-relaxed font-light">
              Premium interior and exterior detailing services designed to
              elevate, protect, and restore your vehicle — treated with the
              precision it deserves.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/booking"
                data-testid={LUXE.heroBookBtn}
                className="btn-book"
              >
                Book Now <ArrowRight size={14} />
              </Link>
              <Link
                to="/services"
                data-testid={LUXE.heroPackagesBtn}
                className="btn-outline-gold"
              >
                View Packages
              </Link>
            </div>
            <div className="mt-14 flex flex-wrap items-center gap-8 text-xs uppercase tracking-[0.24em] text-white/50">
              <span className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-[#C9A24A]" /> Insured Service
              </span>
              <span className="flex items-center gap-2">
                <Award size={14} className="text-[#C9A24A]" /> Professional Quality
              </span>
              <span className="flex items-center gap-2">
                <MapPin size={14} className="text-[#C9A24A]" /> Mobile Convenience
              </span>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-4">
            <Reveal delay={200}>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full border border-[#C9A24A]/40" />
                <img
                  src={IMAGES.exteriorPolish}
                  alt="Exterior polish"
                  className="relative w-full h-[520px] object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#0B0F1A] border border-[#C9A24A]/30 p-6 max-w-[220px]">
                  <div className="package-price text-4xl">$35</div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mt-2">
                    Starting Essential Shine Wash
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 text-[10px] uppercase tracking-[0.32em]">
          Scroll to Explore ↓
        </div>
      </section>

      {/* PROMO FEATURE — 4th of July */}
      <PromoBanner variant="feature" />

      {/* QUICK SERVICES STRIP */}
      <section className="py-24 sm:py-32 border-b border-white/5">
        <div className="luxe-container">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
              <div>
                <div className="luxe-eyebrow mb-4">What We Offer</div>
                <h2 className="text-3xl sm:text-5xl leading-tight text-white max-w-2xl">
                  Craft, care, and finish — <span className="italic text-[#C9A24A]">tailored</span> to every vehicle.
                </h2>
              </div>
              <Link
                to="/services"
                data-testid="home-view-all-services"
                className="text-xs uppercase tracking-[0.28em] text-[#C9A24A] hover:text-white transition-colors flex items-center gap-2"
              >
                View All Services <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 120}>
                  <Link
                    to={s.to}
                    data-testid={s.testId}
                    className="luxe-card group block relative overflow-hidden"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[900ms]"
                      />
                    </div>
                    <div className="p-8">
                      <Icon size={28} className="text-[#C9A24A] mb-5" />
                      <h3 className="text-2xl text-white mb-3">{s.title}</h3>
                      <p className="text-white/60 text-sm leading-relaxed mb-6">
                        {s.desc}
                      </p>
                      <span className="text-xs uppercase tracking-[0.24em] text-[#C9A24A] flex items-center gap-2 group-hover:gap-4 transition-all">
                        Learn More <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PACKAGES */}
      <section className="py-24 sm:py-32">
        <div className="luxe-container">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="luxe-eyebrow mb-4">Signature Packages</div>
              <h2 className="text-3xl sm:text-5xl leading-tight text-white">
                Precision-crafted <span className="italic text-[#C9A24A]">detailing</span> tiers.
              </h2>
              <div className="gold-divider mx-auto mt-6" />
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PACKAGES.filter((p) => p.id !== 'exterior').map((p, i) => (
              <Reveal key={p.id} delay={i * 100}>
                <div
                  data-testid={p.testId}
                  className={`luxe-card p-8 h-full flex flex-col ${
                    p.highlight ? 'border-[#C9A24A]/70 shadow-[0_0_40px_-15px_rgba(201,162,74,0.4)]' : ''
                  }`}
                >
                  {p.highlight && (
                    <span className="luxe-eyebrow text-[10px] mb-3">Most Popular</span>
                  )}
                  <h3 className="text-2xl text-white mb-2">{p.name}</h3>
                  <p className="text-white/50 text-sm mb-6">{p.tagline}</p>

                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="package-price text-4xl">${p.priceSedan}</span>
                    <span className="text-white/40 text-xs uppercase tracking-widest">
                      Sedan
                    </span>
                    <span className="text-white/30">/</span>
                    <span className="package-price text-2xl">${p.priceSuv}</span>
                    <span className="text-white/40 text-xs uppercase tracking-widest">
                      SUV
                    </span>
                  </div>

                  <ul className="space-y-2 mb-8 flex-1">
                    {p.features.slice(0, 4).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-white/70 text-sm">
                        <Check size={14} className="text-[#C9A24A] mt-1 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={`/booking?package=${encodeURIComponent(p.name)}`}
                    data-testid={`${p.testId}-book-btn`}
                    className="btn-outline-gold w-full !py-3 text-[10px]"
                  >
                    Book This
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 sm:py-32 bg-gradient-to-b from-transparent to-[#0d1220]">
        <div className="luxe-container grid lg:grid-cols-12 gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 border border-[#C9A24A]/40" />
              <img
                src={IMAGES.interiorDetail}
                alt="Luxury interior"
                className="relative w-full h-[560px] object-cover"
              />
              <div className="absolute -bottom-8 -right-8 bg-[#C9A24A] p-8 max-w-[220px] text-[#0B0F1A]">
                <div className="text-5xl font-serif font-semibold leading-none">100%</div>
                <div className="text-[10px] uppercase tracking-[0.24em] mt-2 font-semibold">
                  Satisfaction Guarantee
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={150}>
            <div className="luxe-eyebrow mb-4">Why Choose Lavish Luxe</div>
            <h2 className="text-3xl sm:text-5xl leading-tight text-white mb-6">
              Obsessive detail. <br />
              <span className="italic text-[#C9A24A]">Uncompromised</span> finish.
            </h2>
            <p className="text-white/60 leading-relaxed mb-10 max-w-lg">
              Every vehicle receives the same white-glove treatment — from the
              stitching in your headliner to the depth of your paint. We use
              professional-grade products and mobile convenience to protect
              what matters.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { icon: ShieldCheck, title: 'Insured Service', desc: 'Fully insured & professional.' },
                { icon: Award, title: 'Premium Products', desc: 'Only top-tier detailing chemistry.' },
                { icon: Car, title: 'Mobile Convenience', desc: 'We come to your driveway.' },
                { icon: Gem, title: 'Luxury Finish', desc: 'Guaranteed showroom results.' },
              ].map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.title}
                    className="flex items-start gap-4 p-5 border border-[#C9A24A]/15 hover:border-[#C9A24A]/60 transition-colors"
                  >
                    <Icon size={22} className="text-[#C9A24A] shrink-0 mt-1" />
                    <div>
                      <div className="text-white text-sm font-medium mb-1">
                        {f.title}
                      </div>
                      <div className="text-white/50 text-xs leading-relaxed">
                        {f.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-24 sm:py-32">
        <div className="luxe-container">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <div className="luxe-eyebrow mb-4">Portfolio</div>
                <h2 className="text-3xl sm:text-5xl text-white max-w-xl">
                  Real transformations. <br />
                  <span className="italic text-[#C9A24A]">Real</span> results.
                </h2>
              </div>
              <Link
                to="/gallery"
                data-testid="home-view-gallery"
                className="text-xs uppercase tracking-[0.28em] text-[#C9A24A] hover:text-white transition-colors flex items-center gap-2"
              >
                Full Gallery <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {GALLERY_IMAGES.slice(0, 8).map((src, i) => (
              <Reveal key={src} delay={i * 60}>
                <div className="gallery-item aspect-square">
                  <img src={src} alt={`Detail ${i + 1}`} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 sm:py-32 border-y border-white/5 bg-[#0d1220]">
        <div className="luxe-container">
          <Reveal>
            <div className="text-center mb-14">
              <div className="luxe-eyebrow mb-4">Client Voices</div>
              <h2 className="text-3xl sm:text-5xl text-white">
                Trusted by <span className="italic text-[#C9A24A]">St. Louis'</span> most discerning drivers.
              </h2>
            </div>
          </Reveal>

          <div className="max-w-3xl mx-auto relative">
            <div
              className="luxe-card p-10 sm:p-14 text-center min-h-[220px] flex flex-col items-center justify-center"
              data-testid={LUXE.testimonialCard}
            >
              <span className="text-6xl text-[#C9A24A] font-serif leading-none mb-4">
                “
              </span>
              <p className="text-xl sm:text-2xl text-white font-serif italic leading-relaxed mb-8">
                {TESTIMONIALS[testimonialIdx].quote}
              </p>
              <div className="gold-divider mb-4" />
              <div className="text-white/80 text-sm">
                {TESTIMONIALS[testimonialIdx].author}
              </div>
              <div className="text-white/40 text-xs uppercase tracking-[0.24em] mt-1">
                {TESTIMONIALS[testimonialIdx].location}
              </div>
            </div>

            <div className="flex items-center justify-center gap-6 mt-8">
              <button
                type="button"
                aria-label="Previous testimonial"
                data-testid="testimonial-prev"
                onClick={() => setTestimonialIdx((i) => (i - 1 + total) % total)}
                className="w-10 h-10 border border-[#C9A24A]/40 text-[#C9A24A] flex items-center justify-center hover:bg-[#C9A24A] hover:text-[#0B0F1A] transition-all"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="flex gap-2">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Testimonial ${i + 1}`}
                    onClick={() => setTestimonialIdx(i)}
                    className={`h-1 transition-all ${
                      i === testimonialIdx
                        ? 'w-8 bg-[#C9A24A]'
                        : 'w-4 bg-white/20 hover:bg-white/40'
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                aria-label="Next testimonial"
                data-testid="testimonial-next"
                onClick={() => setTestimonialIdx((i) => (i + 1) % total)}
                className="w-10 h-10 border border-[#C9A24A]/40 text-[#C9A24A] flex items-center justify-center hover:bg-[#C9A24A] hover:text-[#0B0F1A] transition-all"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative py-28 overflow-hidden">
        <div
          className="absolute inset-0 hero-parallax"
          style={{ backgroundImage: `url(${IMAGES.ctaBanner})` }}
        />
        <div className="absolute inset-0 bg-[#0B0F1A]/85" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,162,74,0.2),transparent_60%)]" />
        <div className="luxe-container relative z-10 text-center max-w-3xl mx-auto">
          <Reveal>
            <div className="luxe-eyebrow mb-4">Ready When You Are</div>
            <h2 className="text-4xl sm:text-6xl text-white leading-tight mb-6">
              Book Your Luxury <br />
              <span className="italic text-[#C9A24A]">Detail</span> Today.
            </h2>
            <p className="text-white/60 mb-10 max-w-xl mx-auto">
              Mobile service across St. Louis. Weekend availability. Come home
              to a vehicle you love again.
            </p>
            <Link
              to="/booking"
              data-testid="cta-schedule-appointment"
              className="btn-gold"
            >
              Schedule Appointment <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
